import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Y } from "../theme";
import { useTheme } from "../useTheme";
import { useLoadingLockState } from "../App";

const BARS = 7;

// Per-bar background.
//   Dark mode: the original yellow alpha gradient (Y on the leading
//     bar; alpha-yellow trailing).
//   Light mode: a stack of turquoise shades stepping from deep teal up
//     to the bright brand turquoise, so the loader reads as multiple
//     shades of teal rather than a single yellow on the yellow page.
const TURQ_SHADES = [
  "rgba(0,107,118,0.25)",
  "rgba(0,127,138,0.40)",
  "rgba(0,150,150,0.55)",
  "rgba(0,170,150,0.65)",
  "rgba(0,184,148,0.75)",
  "rgba(6,202,160,0.85)",
  "rgba(6,214,160,0.95)",
];
function barColor(mode, i, leadIdx) {
  if (i === leadIdx) return mode === "light" ? "#06D6A0" : Y;
  if (mode === "light") return TURQ_SHADES[i % TURQ_SHADES.length];
  return `rgba(238,255,68,${0.15 + (i/BARS)*0.25})`;
}

function CyberBars() {
  const [tick, setTick] = useState(0);
  const { mode } = useTheme();
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 120);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:3,height:28}}>
      {Array.from({length:BARS}).map((_,i) => {
        const phase = (tick + i * 2) % (BARS * 2);
        const active = phase < BARS;
        const height = active ? 8 + (i % 3) * 8 : 4;
        return (
          <div key={i} style={{
            width: 3,
            height,
            borderRadius: 2,
            background: barColor(mode, i, tick % BARS),
            transition: "height 0.12s ease, background 0.12s ease",
          }}/>
        );
      })}
    </div>
  );
}

/**
 * Page loading overlay.
 *
 * - Renders the spinner via a portal into document.body (viewport-centered, independent of content tree)
 * - Hides the parent container until loading finishes
 * - Flow: ready → hold 500ms → fade out bars (300ms) → fade in content (400ms) → unmount
 */
export default function PageLoader({ ready }) {
  const [phase, setPhase] = useState("loading"); // loading | fadeBars | fadeIn | done
  const parentRef = React.useRef(null);
  const markerRef = React.useRef(null);
  // Combined ready: own `ready` prop AND no descendants currently holding
  // a loading lock (auth image fetches, etc.) so the panel never reveals
  // mid-load. Falls back to just `ready` outside a LoadingLockProvider.
  const { pending } = useLoadingLockState();
  const isReady = ready && pending === 0;

  // Grab the parent container on mount and hide it
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    const parent = marker.parentElement;
    parentRef.current = parent;
    if (parent) {
      parent.style.opacity = "0";
      parent.style.transition = "none";
    }
    return () => {
      // Ensure content is visible on unmount
      if (parentRef.current) {
        parentRef.current.style.opacity = "1";
        parentRef.current.style.transition = "none";
      }
    };
  }, []);

  // isReady → hold 500ms → fadeBars. If isReady flips back to false while
  // we're holding (a new lock came in), we cancel and stay in loading.
  useEffect(() => {
    if (isReady && phase === "loading") {
      const t = setTimeout(() => setPhase("fadeBars"), 500);
      return () => clearTimeout(t);
    }
  }, [isReady, phase]);

  // fadeBars (300ms) → fadeIn
  useEffect(() => {
    if (phase === "fadeBars") {
      const t = setTimeout(() => setPhase("fadeIn"), 300);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // fadeIn → reveal parent, then done
  useEffect(() => {
    if (phase === "fadeIn") {
      const parent = parentRef.current;
      if (parent) {
        parent.style.transition = "opacity 0.4s ease-out";
        parent.style.opacity = "1";
      }
      const t = setTimeout(() => setPhase("done"), 450);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // Safety: auto-dismiss after 5 seconds
  useEffect(() => {
    const t = setTimeout(() => {
      setPhase(p => p === "done" ? p : "fadeBars");
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  // Invisible marker element so we can find the parent container
  const marker = <span ref={markerRef} style={{display:"none"}}/>;

  if (phase === "done") return null;

  const showBars = phase === "loading";

  // Portal: render spinner directly into document.body, centered on viewport
  const overlay = ReactDOM.createPortal(
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    }}>
      <div style={{
        opacity: showBars ? 1 : 0,
        transition: "opacity 0.3s ease-out",
      }}>
        <CyberBars/>
      </div>
    </div>,
    document.body
  );

  return <>{marker}{overlay}</>;
}
